import { TimeSeriesChartDataItem } from 'utils/components/TimeSeriesChart/TimeSeriesChartData';
import {
    applyShowDataFor,
    ShowDataFor,
} from 'utils/components/ShowDataFor/ShowDataFor';
import {
    mapFromElementRangeArray,
    TimeSeriesDisableRange,
} from 'utils/timeseries/TimeSeriesDisableRange';
import { v4 as uuid } from 'uuid';
import { timeUtils } from '@instinet-analytics/core-lib';
import { FillsGraphDataState } from 'widgets/fillsGraph/store/data/FillsGraphDataState';
import {
    getInterpolatedValue,
    InterpolationContext,
    toValueWithInterpolation,
    ValueWithInterpolation,
} from 'utils/interpolation';
import {
    FillsGraphDataOrderStat,
    OrderFill,
} from 'widgets/fillsGraph/fillsGraphData';
import isNil from 'lodash/isNil';
import {
    MarketDataBarItem,
    MarketDataRefData,
} from 'marketData/store/MarketDataState';
import { enrichTimeSeriesWithExternalMarketData } from 'widgets/fillsGraph/marketData';

export const deriveRelativePerfIndex = (d: PerfVsIndexDataTimeSeriesItem) =>
    getInterpolatedValue(d.relativePerfIndex);
export const deriveRelativePerfOrderToIndex = (
    d: PerfVsIndexDataTimeSeriesItem
) => getInterpolatedValue(d.relativePerfOrderToIndex);

const { mapEpochToTime } = timeUtils;

export interface PerfVsIndexData {
    breaksInTrading: TimeSeriesDisableRange[];
    timeSeries: PerfVsIndexDataTimeSeriesItem[];
    startTime: number;
    endTime: number;
    id: string;
    tickSize?: number | null;
    lastNotNilItem?: PerfVsIndexDataTimeSeriesItem;
}

export interface PerfVsIndexDataTimeSeriesItem extends TimeSeriesChartDataItem {
    relativePerfIndex?: ValueWithInterpolation;
    relativePerfOrderToIndex?: ValueWithInterpolation;
}

interface BuildPerfVsIndexDataOptions {
    streamData: FillsGraphDataState | undefined;
    showDataFor?: ShowDataFor;
    orderStatData?: FillsGraphDataOrderStat;
    dapiPriceData?: MarketDataBarItem[];
    dapiRefData?: MarketDataRefData;
    dapiIndexPriceData?: MarketDataBarItem[];
    dapiIndexRefData?: MarketDataRefData;
}

export function buildPerfVsIndexData(
    options: BuildPerfVsIndexDataOptions
): PerfVsIndexData | undefined {
    const {
        streamData,
        showDataFor,
        orderStatData,
        dapiPriceData,
        dapiRefData,
        dapiIndexPriceData,
        dapiIndexRefData,
    } = options;
    const { timeSeries, lastNotNilItem } = buildTimeSeries(
        streamData,
        showDataFor
    );

    if (!timeSeries || timeSeries.length === 0) {
        return undefined;
    }

    if (dapiPriceData) {
        enrichTimeSeriesWithExternalMarketData(
            timeSeries,
            dapiPriceData,
            dapiRefData,
            dapiIndexPriceData,
            dapiIndexRefData,
            orderStatData
        );
    }

    const breaksInTrading = mapFromElementRangeArray(streamData?.disableRange);
    const tickSize = streamData?.orderStat?.tickSize;

    return {
        id: uuid(),
        breaksInTrading,
        lastNotNilItem,
        timeSeries,
        tickSize,
        startTime: timeSeries[0].time,
        endTime: timeSeries[timeSeries.length - 1].time,
    };
}

function buildTimeSeries(
    streamData: FillsGraphDataState | undefined,
    showDataFor?: ShowDataFor
): {
    timeSeries?: PerfVsIndexDataTimeSeriesItem[];
    lastNotNilItem?: PerfVsIndexDataTimeSeriesItem;
} {
    if (!streamData?.timeSeries?.length) {
        return {};
    }

    const sorted = streamData.timeSeries.sort((a, b) =>
        a._i > b._i ? +1 : -1
    );

    const truncatedTimeSeries = applyShowDataFor(sorted, showDataFor);

    const withValues = truncatedTimeSeries.filter(
        (f) => !isNil(f.ich) || !isNil(f.rch)
    );
    const lastNotNilTimeSeriesItem = withValues[withValues.length - 1];
    const targetInteroplationIndex =
        truncatedTimeSeries.indexOf(lastNotNilTimeSeriesItem) ??
        truncatedTimeSeries.length - 1;

    const interpolationContext = new InterpolationContext<OrderFill>(
        'backward'
    );

    const timeSeries: PerfVsIndexDataTimeSeriesItem[] = [];

    for (let i = truncatedTimeSeries.length - 1; i >= 0; i--) {
        const d = truncatedTimeSeries[i];

        timeSeries[i] = {
            time: mapEpochToTime(d._i),
            relativePerfIndex: toValueWithInterpolation(d.ich),
            relativePerfOrderToIndex: toValueWithInterpolation(d.rch),
        };

        interpolationContext.interpolateStep(
            timeSeries,
            ['relativePerfOrderToIndex', 'relativePerfIndex'],
            i,
            targetInteroplationIndex
        );
    }

    return {
        timeSeries,
        lastNotNilItem: lastNotNilTimeSeriesItem
            ? timeSeries[targetInteroplationIndex]
            : undefined,
    };
}
